import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, List, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { TemplateLanguage, TemplateOverview } from '../../../../api/hms';
import type { Action } from '../../hmsActions';
import { LanguageCard } from './LanguageCard';
import { ApprovalChoice, type ApprovalFormState } from '../../components/ApprovalChoice';
import { PendingActions } from '../../components/PendingActions';
import { formatDay } from '../../hmsFormat';
import { formatTemplateChange, sendWarning, versionStatus } from '../riskFactorsView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

export const APPROVAL_COMMENT_ID = 'hr-rf-approval-comment';

type Props = {
  overview: TemplateOverview;
  viewId: number | null;
  onView: (id: number) => void;
  approval: ApprovalFormState;
  send: Action;
  discard: Action;
  approve: Action<[id: number, comment: string]>;
  withdraw: Action<[id: number]>;
  actionFailed: boolean;
  saveLanguages: Action<[languages: TemplateLanguage[], onSaved?: () => void]> & { failed: boolean };
  onTranslate: (code: string) => void;
};

export const TemplateSidePanel = ({
  overview,
  viewId,
  onView,
  approval,
  send,
  discard,
  approve,
  withdraw,
  actionFailed,
  saveLanguages,
  onTranslate,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { draft, current } = overview;
  const pending = draft?.status === 'PENDING' ? draft : null;
  const unsent = draft?.status === 'DRAFT' ? draft : null;
  const approvedVersions = overview.versions.filter((version) => version.status === 'APPROVED');
  const day = (value: string) => formatDay(value, t, i18n.language);

  const warning = unsent ? sendWarning(unsent, current, t) : '';

  return (
    <div className={layout.side} data-sticky="false">
      <LanguageCard
        languages={(draft ?? current).languages}
        selectable={overview.selectableLanguages}
        locked={Boolean(pending)}
        saving={saveLanguages.pending}
        saveFailed={saveLanguages.failed}
        onSave={saveLanguages.run}
        onTranslate={onTranslate}
      />
      <section aria-labelledby="hr-rf-versions" className={layout.sideCard}>
        <Heading level={2} data-size="xs" id="hr-rf-versions">
          {t('hrRiskFactors.versions.title')}
        </Heading>
        <ol className={classes.versions}>
          {pending && (
            <li>
              <div className={classes.versionItem}>
                <span className={classes.versionTitle}>
                  <span className="ds-label" data-size="sm">
                    {t('hrRiskFactors.versions.version', { version: pending.version })}
                  </span>
                  <Tag data-size="sm" data-color="warning">
                    {t('hrRiskFactors.status.pending')}
                  </Tag>
                </span>
                <span className={`ds-paragraph ${layout.subtle}`} data-size="xs">
                  {t('hrRiskFactors.versions.sent', { date: day(pending.submittedAt ?? pending.createdAt) })}
                </span>
              </div>
            </li>
          )}
          {approvedVersions.map((version) => {
            const status = versionStatus(version);
            const shown = version.id === viewId;
            return (
              <li key={version.id}>
                <button
                  type="button"
                  className={classes.versionItem}
                  data-interactive="true"
                  aria-current={shown || undefined}
                  onClick={() => onView(version.id)}
                >
                  <span className={classes.versionTitle}>
                    <span className="ds-label" data-size="sm">
                      {t('hrRiskFactors.versions.version', { version: version.version })}
                    </span>
                    <Tag data-size="sm" data-color={status.color}>
                      {t(status.label)}
                    </Tag>
                  </span>
                  <span className={`ds-paragraph ${layout.subtle}`} data-size="xs">
                    {t('hrRiskFactors.versions.approved', { date: day(version.approval?.approvedAt ?? version.createdAt) })}
                  </span>
                  <span className={`ds-paragraph ${classes.versionCta}`} data-size="xs">
                    {t(shown ? 'hrRiskFactors.versions.shown' : 'hrRiskFactors.versions.show')}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <Paragraph data-size="xs" className={layout.subtle}>
          {t('hrRiskFactors.versions.footnote')}
        </Paragraph>
      </section>

      {unsent && (
        <section aria-labelledby="hr-rf-send" className={layout.sideCard} data-highlight="true">
          <Heading level={2} data-size="xs" id="hr-rf-send">
            {t('hrRiskFactors.send.title', { version: unsent.version })}
          </Heading>
          <List.Unordered data-size="sm">
            {unsent.changes.map((change) => {
              const text = formatTemplateChange(change, t, true, unsent.languages);
              return <List.Item key={text}>{text}</List.Item>;
            })}
          </List.Unordered>
          {warning && (
            <Alert data-color="warning">
              <Paragraph data-size="sm">{warning}</Paragraph>
            </Alert>
          )}
          <Paragraph data-size="xs" className={layout.subtle}>
            {t('hrRiskFactors.send.comparison')}
          </Paragraph>
          <ApprovalChoice
            name="hr-rf-approval"
            legend={t('hrRiskFactors.send.approval')}
            commentId={APPROVAL_COMMENT_ID}
            commentDescription={t('hrRiskFactors.send.commentDescription')}
            {...approval}
          />
          {actionFailed && (
            <Alert data-color="danger">
              <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
            </Alert>
          )}
          <div className={classes.stackedActions}>
            <Button variant="primary" onClick={send.run} loading={send.pending}>
              {approval.mode === 'SAFETY_REP'
                ? t('hrRiskFactors.send.toSafetyRep')
                : t('hrRiskFactors.send.approve', { version: unsent.version })}
            </Button>
            <Button variant="secondary" onClick={discard.run} loading={discard.pending}>
              {t('hrRiskFactors.send.discard')}
            </Button>
          </div>
        </section>
      )}

      {pending && (
        <section aria-labelledby="hr-rf-pending" className={layout.sideCard} data-gap="sm">
          <Heading level={2} data-size="xs" id="hr-rf-pending">
            {t('hrRiskFactors.pending.title')}
          </Heading>
          <Paragraph data-size="sm">{t('hrRiskFactors.pending.text', { version: pending.version })}</Paragraph>
          {actionFailed && (
            <Alert data-color="danger">
              <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
            </Alert>
          )}
          <PendingActions
            id="hr-rf-pending-comment"
            ownVersion={overview.pendingIsYours}
            approve={{ run: (comment) => approve.run(pending.id, comment), pending: approve.pending }}
            withdraw={{ run: () => withdraw.run(pending.id), pending: withdraw.pending }}
          />
        </section>
      )}

      <section aria-labelledby="hr-rf-tips" className={layout.sideCard} data-gap="sm">
        <Heading level={2} data-size="xs" id="hr-rf-tips">
          {t('hrRiskFactors.tips.title')}
        </Heading>
        <List.Unordered data-size="sm">
          {(['one', 'own', 'absolute', 'period'] as const).map((key) => (
            <List.Item key={key}>{t(`hrRiskFactors.tips.${key}`)}</List.Item>
          ))}
        </List.Unordered>
        <Paragraph data-size="xs" className={layout.subtle}>
          {t('hrRiskFactors.tips.footnote')}
        </Paragraph>
      </section>
    </div>
  );
};
